import React, { useState, useMemo } from "react";
import { useHealthStore } from "./storeSalud";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend
} from "recharts";

export default function AdminPanel() {
  const { tickets, centers, startAttention, finishAttention } = useHealthStore();

  const [tab, setTab] = useState("operativo");
  const [selectedCenterId, setSelectedCenterId] = useState("all");

  const primary = "#0A4D8C";
  const green = "#1FAF8F";
  const danger = "#c0392b";

  const filteredTickets = useMemo(() => {
    if (selectedCenterId === "all") return tickets;
    return tickets.filter(t => t.centerId === Number(selectedCenterId));
  }, [tickets, selectedCenterId]);

  const total = filteredTickets.length;
  const pendientes = filteredTickets.filter(t => t.status === "pendiente").length;
  const enAtencion = filteredTickets.filter(t => t.status === "en_atencion").length;
  const finalizados = filteredTickets.filter(t => t.status === "finalizado").length;
  const cancelados = filteredTickets.filter(t => t.status === "cancelado").length;

  const tiemposAtencion = filteredTickets
    .filter(t => t.startedAt && t.finishedAt)
    .map(t => t.finishedAt - t.startedAt);

  const tiempoPromedioAtencion =
    tiemposAtencion.length
      ? tiemposAtencion.reduce((a, b) => a + b, 0) / tiemposAtencion.length
      : 0;

  const tiemposEspera = filteredTickets
    .filter(t => t.startedAt)
    .map(t => t.startedAt - t.createdAt);

  const tiempoPromedioEspera =
    tiemposEspera.length
      ? tiemposEspera.reduce((a, b) => a + b, 0) / tiemposEspera.length
      : 0;

  const formatMinutes = ms => (ms / 60000).toFixed(1);

  // Datos para gráfico de estados
  const dataEstados = [
    { name: "Pendientes", value: pendientes },
    { name: "En Atención", value: enAtencion },
    { name: "Finalizados", value: finalizados },
    { name: "Cancelados", value: cancelados }
  ];

  const colores = ["#f39c12", "#2980b9", green, danger];

  // Análisis por servicio
  const dataServicios = useMemo(() => {
    const map = {};

    filteredTickets.forEach(t => {
      const center = centers.find(c => c.id === t.centerId);
      if (!center) return;

      const service = center.services.find(s => s.id === t.serviceId);
      if (!service) return;

      if (!map[service.name]) {
        map[service.name] = {
          servicio: service.name,
          pendientes: 0,
          esperaPromedio: []
        };
      }

      if (t.status === "pendiente") map[service.name].pendientes++;
      if (t.startedAt)
        map[service.name].esperaPromedio.push(t.startedAt - t.createdAt);
    });

    return Object.values(map).map(s => ({
      servicio: s.servicio,
      pendientes: s.pendientes,
      espera: s.esperaPromedio.length
        ? (s.esperaPromedio.reduce((a, b) => a + b, 0) / s.esperaPromedio.length) / 60000
        : 0
    }));
  }, [filteredTickets, centers]);

  const tabStyle = active => ({
    flex: 1,
    padding: 12,
    textAlign: "center",
    cursor: "pointer",
    background: active ? primary : "white",
    color: active ? "white" : primary,
    border: `1px solid ${primary}`
  });

  return (
    <div style={{ padding: 20, fontFamily: "Arial", background: "#F4F6F8" }}>
      <h2 style={{ color: primary }}>Panel Municipal de Salud</h2>

      <select
        value={selectedCenterId}
        onChange={e => setSelectedCenterId(e.target.value)}
        style={{ padding: 10, marginBottom: 20 }}
      >
        <option value="all">Todos los Centros</option>
        {centers.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>

      <div style={{ display: "flex", marginBottom: 20 }}>
        <div style={tabStyle(tab === "operativo")} onClick={() => setTab("operativo")}>Operativo</div>
        <div style={tabStyle(tab === "estadisticas")} onClick={() => setTab("estadisticas")}>Estadísticas</div>
        <div style={tabStyle(tab === "cuellos")} onClick={() => setTab("cuellos")}>Cuellos de Botella</div>
      </div>

      {tab === "operativo" && (
        <div style={{ background: "white", padding: 20 }}>
            {filteredTickets.length === 0 && (
            <p>No hay fichas registradas.</p>
            )}

            {filteredTickets.map(t => {
            const center = centers.find(c => c.id === t.centerId);
            if (!center) return null;

            const service = center.services.find(s => s.id === t.serviceId);
            if (!service) return null;

            return (
                <div
                key={t.id}
                style={{
                    marginBottom: 15,
                    borderBottom: "1px solid #eee",
                    paddingBottom: 10
                }}
                >
                <strong>{center.name}</strong>
                <p>{service.name} - {t.slot}</p>
                <p>Paciente: {t.name}</p>
                <p>Estado: {t.status}</p>

                {t.status === "pendiente" && (
                    <button onClick={() => startAttention(t.id)}>
                    Iniciar Atención
                    </button>
                )}

                {t.status === "en_atencion" && (
                    <button onClick={() => finishAttention(t.id)}>
                    Finalizar Atención
                    </button>
                )}
                </div>
            );
            })}
        </div>
        )}

      {tab === "estadisticas" && (
        <div style={{ background: "white", padding: 20 }}>
          <h3>Distribución de Estados</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={dataEstados} dataKey="value" outerRadius={100} label>
                {dataEstados.map((entry, index) => (
                  <Cell key={index} fill={colores[index]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

          <h3 style={{ marginTop: 40 }}>Tiempos Promedio (min)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                { name: "Espera", value: formatMinutes(tiempoPromedioEspera) },
                { name: "Atención", value: formatMinutes(tiempoPromedioAtencion) }
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill={primary} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {tab === "cuellos" && (
        <div style={{ background: "white", padding: 20 }}>
          <h3>Espera Promedio por Servicio (min)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataServicios}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="servicio" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="espera" fill="#e67e22" />
            </BarChart>
          </ResponsiveContainer>

          <h3 style={{ marginTop: 40 }}>Pendientes por Servicio</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataServicios}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="servicio" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="pendientes" fill={danger} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}